import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
  Button,
  Badge,
} from "@asm/ui";
import {
  Route,
  RefreshCw,
  Shield,
  ArrowRight,
  Globe,
  Server,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Cloud,
} from "lucide-react";
import { riskApi, type AttackPath } from "@/lib/api";

// Cloud provider icons mapping
const CLOUD_PROVIDER_ICONS: Record<string, { icon: string; name: string }> = {
  aws: { icon: "/icons/aws.svg", name: "AWS" },
  amazon: { icon: "/icons/aws.svg", name: "AWS" },
  azure: { icon: "/icons/azure.svg", name: "Azure" },
  microsoft: { icon: "/icons/azure.svg", name: "Azure" },
  gcp: { icon: "/icons/gcp.svg", name: "GCP" },
  google: { icon: "/icons/gcp.svg", name: "GCP" },
  cloudflare: { icon: "/icons/cloudflare.svg", name: "Cloudflare" },
  digitalocean: { icon: "/icons/digitalocean.svg", name: "DigitalOcean" },
};

// AWS service-specific icons
const AWS_SERVICE_ICONS: Record<string, { icon: string; name: string }> = {
  cloudfront: { icon: "/icons/aws-cloudfront.svg", name: "AWS CloudFront" },
  ec2: { icon: "/icons/aws-ec2.svg", name: "AWS EC2" },
  s3: { icon: "/icons/aws-s3.svg", name: "AWS S3" },
  lambda: { icon: "/icons/aws-lambda.svg", name: "AWS Lambda" },
  elb: { icon: "/icons/aws-elb.svg", name: "AWS ELB" },
};

function getCloudInfo(
  provider: string | null | undefined,
  service: string | null | undefined
): { icon: string; name: string } | null {
  if (!provider) return null;
  const providerKey = provider.toLowerCase().replace(/[^a-z]/g, "");

  if (providerKey === "aws" || providerKey === "amazon") {
    if (service) {
      const serviceKey = service.toLowerCase().replace(/[^a-z]/g, "");
      if (AWS_SERVICE_ICONS[serviceKey]) return AWS_SERVICE_ICONS[serviceKey];
    }
    return CLOUD_PROVIDER_ICONS.aws;
  }

  for (const [k, v] of Object.entries(CLOUD_PROVIDER_ICONS)) {
    if (providerKey.includes(k)) return v;
  }
  return null;
}
import { useState } from "react";
import { cn } from "@asm/ui";

export default function AttackPaths() {
  const {
    data: attackPaths,
    isLoading,
    refetch,
    error,
  } = useQuery({
    queryKey: ["attack-paths"],
    queryFn: () => riskApi.getAttackPaths({ limit: 50 }),
    staleTime: 30_000,
  });

  // Group paths by severity
  const groupedPaths = attackPaths?.paths.reduce(
    (acc, path) => {
      const severity = path.severity.toLowerCase();
      if (!acc[severity]) acc[severity] = [];
      acc[severity].push(path);
      return acc;
    },
    {} as Record<string, AttackPath[]>
  );

  const severityOrder = ["critical", "high", "medium", "low"];

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Route className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold">Unable to load attack paths</h3>
        <p className="text-muted-foreground mt-1">
          {error instanceof Error ? error.message : "An error occurred"}
        </p>
        <Button onClick={() => refetch()} variant="outline" className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <Card className="border-blue-500/20 bg-blue-500/5">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <Lightbulb className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium">Understanding Attack Paths</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Attack paths show potential ways an attacker could exploit exposed services
                to gain access to your infrastructure. Each path traces from an entry point
                (subdomain or IP) through an exposed service to potential impact.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard
          title="Total Paths"
          value={attackPaths?.total || 0}
          severity="neutral"
          loading={isLoading}
        />
        <StatCard
          title="Critical"
          value={groupedPaths?.critical?.length || 0}
          severity="critical"
          loading={isLoading}
        />
        <StatCard
          title="High"
          value={groupedPaths?.high?.length || 0}
          severity="high"
          loading={isLoading}
        />
        <StatCard
          title="Medium"
          value={groupedPaths?.medium?.length || 0}
          severity="medium"
          loading={isLoading}
        />
      </div>

      {/* Attack Paths by Severity */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      ) : !attackPaths || attackPaths.paths.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="flex flex-col items-center justify-center text-center text-muted-foreground">
              <Shield className="h-12 w-12 opacity-50 mb-4" />
              <p className="font-medium">No attack paths identified</p>
              <p className="text-sm mt-1">
                Your attack surface appears well-protected. Keep monitoring for changes.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {severityOrder.map((severity) => {
            const paths = groupedPaths?.[severity];
            if (!paths || paths.length === 0) return null;

            return (
              <SeveritySection
                key={severity}
                severity={severity}
                paths={paths}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function SeveritySection({
  severity,
  paths,
}: {
  severity: string;
  paths: AttackPath[];
}) {
  const [expanded, setExpanded] = useState(severity === "critical" || severity === "high");

  const getSeverityStyles = () => {
    switch (severity) {
      case "critical":
        return {
          border: "border-red-500/30",
          bg: "bg-red-500/5",
          badge: "bg-red-500 text-white",
          icon: "text-red-500",
        };
      case "high":
        return {
          border: "border-orange-500/30",
          bg: "bg-orange-500/5",
          badge: "bg-orange-500 text-white",
          icon: "text-orange-500",
        };
      case "medium":
        return {
          border: "border-yellow-500/30",
          bg: "bg-yellow-500/5",
          badge: "bg-yellow-500 text-black",
          icon: "text-yellow-500",
        };
      default:
        return {
          border: "border-blue-500/30",
          bg: "bg-blue-500/5",
          badge: "bg-blue-500 text-white",
          icon: "text-blue-500",
        };
    }
  };

  const styles = getSeverityStyles();

  return (
    <Card className={cn(styles.border)}>
      <CardHeader
        className={cn("cursor-pointer", styles.bg)}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className={cn("h-5 w-5", styles.icon)} />
            <div>
              <CardTitle className="capitalize">{severity} Severity</CardTitle>
              <CardDescription>
                {paths.length} potential attack {paths.length === 1 ? "path" : "paths"}
              </CardDescription>
            </div>
          </div>
          <Button variant="ghost" size="sm">
            {expanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-6">
          <div className="space-y-4">
            {paths.map((path) => (
              <AttackPathCard key={path.id} path={path} />
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function AttackPathCard({ path }: { path: AttackPath }) {
  const [showRemediation, setShowRemediation] = useState(false);
  const cloudInfo = path.entry_point_type === "ip"
    ? getCloudInfo(path.cloud_provider, path.cloud_region)
    : null;

  return (
    <div className="p-4 rounded-lg border bg-card">
      {/* Attack Chain Visualization */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        {/* Entry Point */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted">
          {path.entry_point_type === "subdomain" ? (
            <Globe className="h-4 w-4 text-blue-500" />
          ) : cloudInfo ? (
            <img src={cloudInfo.icon} alt={cloudInfo.name} className="h-4 w-4" />
          ) : path.is_cloud ? (
            <Cloud className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Server className="h-4 w-4 text-green-500" />
          )}
          <span className="font-mono text-sm">{path.entry_point}</span>
          {cloudInfo && (
            <span className="text-xs text-blue-500">{cloudInfo.name}</span>
          )}
        </div>

        <ArrowRight className="h-4 w-4 text-muted-foreground" />

        {/* Service */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted">
          <span className="font-mono text-sm">
            :{path.service.port}/{path.service.protocol}
          </span>
          {path.service.service_name && (
            <Badge variant="outline" className="text-xs">
              {path.service.service_name}
              {path.service.service_version && ` ${path.service.service_version}`}
            </Badge>
          )}
        </div>

        {/* Vulnerability (if present) */}
        {path.vulnerability && (
          <>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span className="font-mono text-sm">{path.vulnerability.cve_id}</span>
              {path.vulnerability.is_kev && (
                <Badge variant="destructive" className="text-xs">KEV</Badge>
              )}
            </div>
          </>
        )}
      </div>

      {/* Remediation */}
      <div>
        <Button
          variant="ghost"
          size="sm"
          className="text-xs"
          onClick={() => setShowRemediation(!showRemediation)}
        >
          <Lightbulb className="h-3 w-3 mr-1" />
          {showRemediation ? "Hide" : "Show"} Remediation
        </Button>
        {showRemediation && (
          <div className="mt-2 p-3 rounded-lg bg-green-500/5 border border-green-500/20">
            <p className="text-sm">{path.remediation}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  severity,
  loading,
}: {
  title: string;
  value: number;
  severity: "critical" | "high" | "medium" | "low" | "neutral";
  loading: boolean;
}) {
  const getValueColor = () => {
    if (value === 0) return "text-muted-foreground";
    switch (severity) {
      case "critical":
        return "text-red-600 dark:text-red-400";
      case "high":
        return "text-orange-600 dark:text-orange-400";
      case "medium":
        return "text-yellow-600 dark:text-yellow-400";
      default:
        return "text-foreground";
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-sm font-medium text-muted-foreground">{title}</div>
        {loading ? (
          <Skeleton className="h-8 w-16 mt-1" />
        ) : (
          <div className={cn("text-2xl font-bold mt-1", getValueColor())}>
            {value}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
